import type { DisplayJob } from '../types';
import { buildFinlandCountryOnlyJobLocationPlace } from './finland-country-job-location-place';
import { BASE_URL } from '../../utils/constants/url-constants';
import { matchesLocation, buildFullCompanyAddress } from '../geocoding/match-job-location-with-company-address';
import { joinDisplayJobTextList } from './join-display-job-text-list';
import { computeJobListingValidThroughMs } from '../job-detail/compute-job-listing-valid-through';
import { formatJobPostingDescriptionHtml } from './format-job-posting-description-html';

export interface JobPostingSchemaOptions {
  jobLocationType?: 'ON_SITE' | 'HYBRID' | 'TELECOMMUTE';
  jobLocations?: Array<{
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  }>;
  applicantLocationRequirements?: Record<string, unknown> | Record<string, unknown>[];
  disableSalary?: boolean;
}

/**
 * Generates a JobPosting schema.org object from a DisplayJob.
 * Returns { schema, isContentComplete, missingFields }
 * - schema: the JSON-LD object
 * - isContentComplete: true if Google-essential fields are filled
 * - missingFields: array of missing field names
 * Accepts locale, an (unused) apply URL, and canonicalUrl (JobCrawls URL for ownership).
 */
export function generateJobPostingSchema(
  job: DisplayJob,
  locale: string = 'fi',
  // Kept for call-site compatibility; the apply URL no longer feeds any schema field (it used
  // to set hiringOrganization.sameAs, which misidentified the employer for ATS-hosted ads).
  _applyUrl?: string,
  canonicalUrl?: string,
  options: JobPostingSchemaOptions = {}
) {
  // Helper: treat empty string as missing
  const clean = <T>(val: T | undefined | null): T | undefined => {
    if (val === undefined || val === null) return undefined;
    if (typeof val === 'string' && val.trim() === '') return undefined;
    if (Array.isArray(val) && val.length === 0) return undefined;
    return val;
  };

  // Some crawled company URLs are two URLs joined by ';' (e.g. a redirect + the real site).
  // schema.org's `url` expects a single value, so take the first one.
  const firstUrl = (raw: string | undefined): string | undefined => {
    const c = clean(raw);
    if (!c) return undefined;
    const first = c.split(';')[0]?.trim();
    return first || undefined;
  };
  const companyURL = firstUrl(job.companyURL);

  // --- Date Handling ---
  // Returns a YYYY-MM-DD string, or null if val is missing/unparseable (never silently
  // substitutes a fallback — callers decide what to try next).
  function parseDateOnly(val: unknown): string | null {
    if (val === undefined || val === null || val === '') return null;
    if (typeof val === 'number') {
      // Assume unix timestamp (seconds or ms)
      const ts = val > 1e12 ? val : val * 1000;
      const d = new Date(ts);
      return isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
    }
    if (typeof val === 'string') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    }
    return null;
  }
  function toISODate(val: unknown, fallback: Date): string {
    return parseDateOnly(val) ?? fallback.toISOString().split('T')[0];
  }
  const today = new Date();
  // datePosted must be a fixed fact about the job, not something that drifts with render time.
  // Prefer the source's own postedDate; when that's missing or unparseable, fall back to
  // firstSeen (the date our crawler first found the listing) rather than today — today's date
  // is a last resort only for the rare case firstSeen is itself missing/unparseable.
  const datePosted = parseDateOnly(job.postedDate) ?? toISODate(job.firstSeen, today);
  // Same computation get-job-listing-http-status-code.ts (200 vs 410) and maintainer.ts
  // (isActive) use, so this schema field can never again claim a listing expired while the page
  // itself still serves it as live — see compute-job-listing-valid-through.ts.
  const validThroughMs = computeJobListingValidThroughMs(
    { postedDate: job.postedDate, lastSeen: job.lastSeen, applicationDeadline: job.applicationDeadline },
    today.getTime(),
  );
  const validThrough = new Date(validThroughMs).toISOString().split('T')[0];

  // --- employmentType mapping to Google's expected enum ---
  // Every value of JOB_TYPE_EN / JOB_TYPE_SV / JOB_TYPE_FI (lambda/utils/processor/locale-enum-fields.ts)
  // must appear here, plus the legacy free-text aliases below it. Google's allowed values are
  // FULL_TIME, PART_TIME, CONTRACTOR, TEMPORARY, INTERN, VOLUNTEER, PER_DIEM, OTHER. An unknown
  // value omits the property rather than claiming FULL_TIME; the processor already defaults
  // jobType to Full-time / Kokoaikainen / Heltid, so an unknown value here is a real data error.
  const employmentTypeMap: Record<string, string> = {
    // English (processor enum)
    'full-time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    'temporary': 'TEMPORARY',
    'summer job': 'TEMPORARY',
    'contract': 'CONTRACTOR',
    'internship': 'INTERN',
    'open application': 'OTHER',
    'freelance': 'CONTRACTOR',
    'apprenticeship': 'OTHER',
    // Finnish (processor enum)
    'kokoaikainen': 'FULL_TIME',
    'osa-aikainen': 'PART_TIME',
    'määräaikainen': 'TEMPORARY',
    'kesätyö': 'TEMPORARY',
    'sopimuspohjainen työ': 'CONTRACTOR',
    'harjoittelupaikka': 'INTERN',
    'avoin hakemus': 'OTHER',
    'keikkatyö': 'CONTRACTOR',
    'oppisopimus': 'OTHER',
    // Swedish (processor enum)
    'heltid': 'FULL_TIME',
    'deltid': 'PART_TIME',
    'tillfällig': 'TEMPORARY',
    'sommarjobb': 'TEMPORARY',
    'kontrakt': 'CONTRACTOR',
    'praktik': 'INTERN',
    'öppen ansökan': 'OTHER',
    'frilans': 'CONTRACTOR',
    'lärlingsplats': 'OTHER',
    // Legacy free-text aliases seen in older records
    'full time': 'FULL_TIME',
    'fulltime': 'FULL_TIME',
    'part time': 'PART_TIME',
    'parttime': 'PART_TIME',
    'contractor': 'CONTRACTOR',
    'intern': 'INTERN',
    'sopimuspohjainen': 'CONTRACTOR',
    'harjoittelija': 'INTERN',
    'tidsbegränsad': 'TEMPORARY',
    'praktikant': 'INTERN',
  };
  const normalizedJobType = ((job.jobType || '').toLowerCase().trim());
  const employmentType: string | undefined = employmentTypeMap[normalizedJobType];

  // --- logoUrl construction (absolute, using WebP 128px) ---
  function getCompanyLogoUrl(companyId: string): string {
    if (!companyId) {
      return `${BASE_URL}/company-logo-default.svg`;
    }
    const kebab = (companyId || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    // Use WebP 128px as default (good balance between quality and size)
    return `${BASE_URL}/logos/${kebab}-128.webp`;
  }
  const logoUrl = getCompanyLogoUrl(job.companyId ?? '');

  // --- jobLocation parsing (array support) ---
  const countryMap: Record<string, string> = {
    'finland': 'FI',
    'suomi': 'FI',
    'sweden': 'SE',
    'sverige': 'SE',
    'norway': 'NO',
    'norge': 'NO',
    'denmark': 'DK',
    'danmark': 'DK',
    'estonia': 'EE',
    'eesti': 'EE',
    'latvia': 'LV',
    'lithuania': 'LT',
    'germany': 'DE',
    'saksa': 'DE',
    'poland': 'PL',
    'france': 'FR',
    'spain': 'ES',
    'italy': 'IT',
    'portugal': 'PT',
    'netherlands': 'NL',
    'belgium': 'BE',
    'austria': 'AT',
    'switzerland': 'CH',
    'swiss': 'CH',
    'ireland': 'IE',
    'united states': 'US',
    'usa': 'US',
    'yhdysvallat': 'US',
    'united kingdom': 'GB',
    'uk': 'GB',
    'england': 'GB',
    'scotland': 'GB',
    'wales': 'GB',
    'northern ireland': 'GB',
    'canada': 'CA',
    'australia': 'AU',
    'new zealand': 'NZ',
    'india': 'IN',
    'singapore': 'SG',
  };

  const isoToCountryName: Record<string, string> = {
    FI: 'Finland',
    SE: 'Sweden',
    NO: 'Norway',
    DK: 'Denmark',
    EE: 'Estonia',
    LV: 'Latvia',
    LT: 'Lithuania',
    DE: 'Germany',
    PL: 'Poland',
    FR: 'France',
    ES: 'Spain',
    IT: 'Italy',
    PT: 'Portugal',
    NL: 'Netherlands',
    BE: 'Belgium',
    AT: 'Austria',
    CH: 'Switzerland',
    IE: 'Ireland',
    US: 'United States',
    GB: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    NZ: 'New Zealand',
    IN: 'India',
    SG: 'Singapore',
  };

  const cityCountryMap: Record<string, string> = {
    'helsinki': 'FI',
    'helsingfors': 'FI',
    'espoo': 'FI',
    'esbo': 'FI',
    'vantaa': 'FI',
    'vanda': 'FI',
    'tampere': 'FI',
    'tammerfors': 'FI',
    'turku': 'FI',
    'åbo': 'FI',
    'abo': 'FI',
    'oulu': 'FI',
    'uleåborg': 'FI',
    'uleaborg': 'FI',
    'lahti': 'FI',
    'lahtis': 'FI',
    'kuopio': 'FI',
    'jyväskylä': 'FI',
    'jyvaskyla': 'FI',
    'pori': 'FI',
    'björneborg': 'FI',
    'bjorneborg': 'FI',
    'lappeenranta': 'FI',
    'villmanstrand': 'FI',
    'kotka': 'FI',
    'joensuu': 'FI',
    'rovaniemi': 'FI',
    'seinäjoki': 'FI',
    'seinajoki': 'FI',
    'mikkeli': 'FI',
    'sankt michel': 'FI',
    's:t michel': 'FI',
    'st. michel': 'FI',
    'st michel': 'FI',
    'hämeenlinna': 'FI',
    'hameenlinna': 'FI',
    'hyvinkää': 'FI',
    'hyvinkaa': 'FI',
    'salo': 'FI',
    'nurmijärvi': 'FI',
    'nurmijarvi': 'FI',
    'kemi': 'FI',
    // TEC-482 spot-check: these Finnish cities were entirely absent (both en/fi and sv names), 
    // a bare single-segment location for one of them (e.g. "Vaasa" with no ", Finland" suffix)
    // silently got no addressCountry at all in the JobPosting schema.
    'vaasa': 'FI',
    'vasa': 'FI',
    'porvoo': 'FI',
    'borgå': 'FI',
    'kokkola': 'FI',
    'karleby': 'FI',
    'kajaani': 'FI',
    'kajana': 'FI',
    'rauma': 'FI',
    'raumo': 'FI',
    'lohja': 'FI',
    'lojo': 'FI',
    'hanko': 'FI',
    'hangö': 'FI',
    'hango': 'FI',
    'kouvola': 'FI',
    'tallinn': 'EE',
    'stockholm': 'SE',
    'gothenburg': 'SE',
    'göteborg': 'SE',
    'malmö': 'SE',
    'malmo': 'SE',
    'oslo': 'NO',
    'copenhagen': 'DK',
    'köbenhavn': 'DK',
    'kobenhavn': 'DK',
    'berlin': 'DE',
    'munich': 'DE',
    'münchen': 'DE',
    'hamburg': 'DE',
    'london': 'GB',
    'dublin': 'IE',
    'paris': 'FR',
    'madrid': 'ES',
    'rome': 'IT',
  };

  // Finnish city -> region (maakunta), used to fill addressRegion when the location
  // string only has city + country (e.g. "Helsinki, Finland") and lacks an explicit region.
  const finlandCityRegionMap: Record<string, string> = {
    'helsinki': 'Uusimaa',
    'helsingfors': 'Uusimaa',
    'espoo': 'Uusimaa',
    'esbo': 'Uusimaa',
    'vantaa': 'Uusimaa',
    'vanda': 'Uusimaa',
    'hyvinkää': 'Uusimaa',
    'hyvinkaa': 'Uusimaa',
    'nurmijärvi': 'Uusimaa',
    'nurmijarvi': 'Uusimaa',
    'tampere': 'Pirkanmaa',
    'tammerfors': 'Pirkanmaa',
    'turku': 'Varsinais-Suomi',
    'åbo': 'Varsinais-Suomi',
    'abo': 'Varsinais-Suomi',
    'salo': 'Varsinais-Suomi',
    'oulu': 'Pohjois-Pohjanmaa',
    'uleåborg': 'Pohjois-Pohjanmaa',
    'uleaborg': 'Pohjois-Pohjanmaa',
    'lahti': 'Päijät-Häme',
    'lahtis': 'Päijät-Häme',
    'kuopio': 'Pohjois-Savo',
    'jyväskylä': 'Keski-Suomi',
    'jyvaskyla': 'Keski-Suomi',
    'pori': 'Satakunta',
    'björneborg': 'Satakunta',
    'bjorneborg': 'Satakunta',
    'lappeenranta': 'Etelä-Karjala',
    'villmanstrand': 'Etelä-Karjala',
    'kotka': 'Kymenlaakso',
    'joensuu': 'Pohjois-Karjala',
    'rovaniemi': 'Lappi',
    'kemi': 'Lappi',
    'seinäjoki': 'Etelä-Pohjanmaa',
    'seinajoki': 'Etelä-Pohjanmaa',
    'mikkeli': 'Etelä-Savo',
    'sankt michel': 'Etelä-Savo',
    's:t michel': 'Etelä-Savo',
    'st. michel': 'Etelä-Savo',
    'st michel': 'Etelä-Savo',
    'hämeenlinna': 'Kanta-Häme',
    'hameenlinna': 'Kanta-Häme',
    // TEC-482 spot-check: same 8-city gap as cityCountryMap above.
    'vaasa': 'Pohjanmaa',
    'vasa': 'Pohjanmaa',
    'porvoo': 'Uusimaa',
    'borgå': 'Uusimaa',
    'kokkola': 'Keski-Pohjanmaa',
    'karleby': 'Keski-Pohjanmaa',
    'kajaani': 'Kainuu',
    'kajana': 'Kainuu',
    'rauma': 'Satakunta',
    'raumo': 'Satakunta',
    'lohja': 'Uusimaa',
    'lojo': 'Uusimaa',
    'hanko': 'Uusimaa',
    'hangö': 'Uusimaa',
    'hango': 'Uusimaa',
    'kouvola': 'Kymenlaakso',
  };

  const remotePrefixes = ['remote', 'etätyö', 'etatyö', 'etä', 'eta', 'distans', 'distansarbete'];
  const hybridPrefixes = ['hybrid', 'hybridityö', 'hybridarbete'];
  const onsitePrefixes = ['onsite', 'on-site', 'on site', 'paikkasidonnainen', 'på plats'];

  type ParsedLocation = {
    type: 'remote' | 'hybrid' | 'onsite' | 'general';
    cleaned: string;
    original: string;
  };

  const parseLocationEntry = (loc: string): ParsedLocation | null => {
    if (!loc || typeof loc !== 'string') return null;
    let cleaned = loc.trim();
    if (!cleaned) return null;
    const lower = cleaned.toLowerCase();

    const stripPrefix = (prefix: string) => {
      cleaned = cleaned.slice(prefix.length).trim();
      if (cleaned.startsWith('-') || cleaned.startsWith(',') || cleaned.startsWith(':')) {
        cleaned = cleaned.slice(1).trim();
      }
    };

    // Longest matching prefix wins, not first-in-array: 'hybridPrefixes' lists 'hybrid' before
    // 'hybridityö', and "Hybridityö - Helsinki".startsWith('hybrid') is also true, so picking
    // the first match stripped only "hybrid" and left the locality as the garbled "ityö -
    // Helsinki". Same latent issue for 'distans' vs 'distansarbete' in remotePrefixes.
    const longestMatchingPrefix = (prefixes: string[]): string | undefined =>
      prefixes.reduce<string | undefined>((best, prefix) => {
        if (!lower.startsWith(prefix)) return best;
        return !best || prefix.length > best.length ? prefix : best;
      }, undefined);

    const remoteMatch = longestMatchingPrefix(remotePrefixes);
    if (remoteMatch) {
      stripPrefix(remoteMatch);
      return { type: 'remote', cleaned, original: loc };
    }

    const hybridMatch = longestMatchingPrefix(hybridPrefixes);
    if (hybridMatch) {
      stripPrefix(hybridMatch);
      return { type: 'hybrid', cleaned, original: loc };
    }

    const onsiteMatch = longestMatchingPrefix(onsitePrefixes);
    if (onsiteMatch) {
      stripPrefix(onsiteMatch);
      return { type: 'onsite', cleaned, original: loc };
    }

    return { type: 'general', cleaned, original: loc };
  };

  const parsedLocations = Array.isArray(job.location)
    ? (job.location as Array<string | undefined | null>)
        .filter((loc): loc is string => typeof loc === 'string' && loc.trim().length > 0)
        .map((loc) => parseLocationEntry(loc))
        .filter((entry): entry is ParsedLocation => entry !== null)
    : [];

  const remoteLocationEntries = parsedLocations.filter((entry) => entry.type === 'remote');
  const hybridLocationEntries = parsedLocations.filter((entry) => entry.type === 'hybrid');
  const physicalLocationEntries = parsedLocations.filter(
    (entry) => entry.type === 'onsite' || entry.type === 'general' || entry.type === 'hybrid'
  );

  const remotenessValue = (job.remoteness || '').toLowerCase();
  const hasRemoteFlag =
    remoteLocationEntries.length > 0 ||
    ['remote', 'etä', 'etätyö', 'distans', 'anywhere', 'global'].some((token) => remotenessValue.includes(token));
  const hasHybridFlag =
    hybridLocationEntries.length > 0 ||
    ['hybrid', 'hybridityö', 'hybridarbete'].some((token) => remotenessValue.includes(token));

  const toCountryCode = (value?: string): string | undefined => {
    if (!value) return undefined;
    const sanitized = value.replace(/\(.*?\)/g, '').trim();
    if (!sanitized) return undefined;
    const lower = sanitized.toLowerCase();
    if (countryMap[lower]) return countryMap[lower];
    if (sanitized.length === 2) return sanitized.toUpperCase();
    return undefined;
  };

  const toTitleCase = (value: string): string =>
    value
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  const buildPlace = (raw: string) => {
    const segments = raw
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean);
    if (segments.length === 0) return null;

    let addressLocality: string | undefined;
    let addressRegion: string | undefined;
    let addressCountry: string | undefined;

    if (segments.length === 1) {
      const segment = segments[0];
      const directCountry = toCountryCode(segment);
      if (directCountry) {
        addressCountry = directCountry;
      } else {
        const cityIso = cityCountryMap[segment.toLowerCase()];
        if (cityIso) {
          addressLocality = toTitleCase(segment);
          addressCountry = cityIso;
        } else {
          addressLocality = toTitleCase(segment);
        }
      }
    } else if (segments.length === 2) {
      addressLocality = segments[0];
      const countryCandidate = toCountryCode(segments[1]) || cityCountryMap[segments[1].toLowerCase()];
      if (countryCandidate) {
        addressCountry = countryCandidate;
      }
    } else if (segments.length >= 3) {
      addressLocality = segments[0];
      addressRegion = segments.slice(1, -1).join(', ');
      const countryCandidate =
        toCountryCode(segments[segments.length - 1]) ||
        cityCountryMap[segments[segments.length - 1].toLowerCase()];
      if (countryCandidate) {
        addressCountry = countryCandidate;
      }
    }

    if (!addressRegion && addressLocality && addressCountry === 'FI') {
      addressRegion = finlandCityRegionMap[addressLocality.toLowerCase()];
    }

    if (!addressLocality && !addressCountry) return null;

    return {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        ...(addressLocality ? { addressLocality } : {}),
        ...(addressRegion ? { addressRegion } : {}),
        ...(addressCountry ? { addressCountry } : {}),
      },
    };
  };

  const placeEntries = physicalLocationEntries
    .map((entry) => buildPlace(entry.cleaned))
    .filter((place): place is Record<string, unknown> => place !== null);
  let jobLocation: Record<string, unknown>[] | undefined = undefined;

  // Check if any job location matches company address (for Finland locations)
  // If so, use the full company address in the schema
  const companyData = job as DisplayJob & {
    address_fi?: string;
    address_sv?: string;
    registryStreet?: string;
    registryBuildingNumber?: string;
    registryPostCode?: string;
    registryCity?: string;
  };
  
  let useFullCompanyAddress = false;
  let matchingLocation: string | undefined;
  
  // Check all physical location entries to see if any match company address
  for (const entry of physicalLocationEntries) {
    const locationString = entry.cleaned || entry.original;
    // Only check Finland locations
    const isFinlandLocation = locationString.toLowerCase().includes('finland') || 
                              locationString.toLowerCase().includes('suomi') ||
                              cityCountryMap[locationString.toLowerCase().split(',')[0]?.trim().toLowerCase()] === 'FI';
    
    if (isFinlandLocation && matchesLocation(locationString, companyData)) {
      useFullCompanyAddress = true;
      matchingLocation = locationString;
      break; // Use first matching location
    }
  }

  const extractRemoteRegions = (entry: ParsedLocation): string[] => {
    const raw = entry.cleaned.trim();
    if (!raw) return [];
    const separators = /(?:,|\/|&|\band\b|\bja\b|\boch\b|\bella\b)/i;
    return raw
      .split(separators)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  };

  const mapRegionToRequirement = (region: string) => {
    const lower = region.toLowerCase();
    // Empty region or explicit worldwide scope → no specific country required; caller omits the field
    if (lower === '' || ['global', 'worldwide', 'anywhere', 'globaali', 'globalt'].includes(lower)) {
      return null;
    }
    if (countryMap[lower]) {
      const iso = countryMap[lower];
      return { '@type': 'Country', name: isoToCountryName[iso] ?? toTitleCase(region) };
    }

    const normalized = lower.replace(/\s+/g, '');
    const continentMap: Record<string, string> = {
      europe: 'Europe',
      europa: 'Europe',
      eurooppa: 'Europe',
      eu: 'Europe',
      asia: 'Asia',
      northamerica: 'North America',
      southamerica: 'South America',
      africa: 'Africa',
      oceania: 'Oceania',
    };
    if (continentMap[normalized]) {
      if (['europe', 'eu', 'europa', 'eurooppa'].includes(normalized)) {
        return { '@type': 'Country', name: 'Finland' };
      }
      return { '@type': 'Continent', name: continentMap[normalized] };
    }

    const administrativeAreaFinland: Set<string> = new Set([
      'emea',
      'nordics',
      'nordic',
      'pohjoismaat',
      'scandinavia',
      'skandinavien',
      'baltics',
      'benelux',
    ]);
    if (administrativeAreaFinland.has(normalized)) {
      return { '@type': 'Country', name: 'Finland' };
    }

    if (region.length === 2) {
      const iso = region.toUpperCase();
      return { '@type': 'Country', name: isoToCountryName[iso] ?? iso };
    }

    const fallbackIso = cityCountryMap[lower];
    if (fallbackIso) {
      return { '@type': 'Country', name: isoToCountryName[fallbackIso] ?? fallbackIso };
    }

    return null;
  };

  const remoteRequirements = remoteLocationEntries
    .flatMap((entry) => extractRemoteRegions(entry).map(mapRegionToRequirement))
    .filter((req): req is Record<string, unknown> => req !== null);

  const remoteOverride = options.jobLocationType === 'TELECOMMUTE';
  const remoteIndicated = hasRemoteFlag || remoteOverride;

  let jobLocationType: 'TELECOMMUTE' | undefined = remoteIndicated ? 'TELECOMMUTE' : undefined;

  const overrideJobLocations =
    options.jobLocations && options.jobLocations.length > 0
      ? options.jobLocations.map((loc) => ({
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            ...(loc.addressLocality ? { addressLocality: loc.addressLocality } : {}),
            ...(loc.addressRegion ? { addressRegion: loc.addressRegion } : {}),
            ...(loc.addressCountry ? { addressCountry: loc.addressCountry } : {}),
          },
        }))
      : undefined;

  if (jobLocationType === 'TELECOMMUTE') {
    jobLocation = overrideJobLocations;
  } else {
    if (overrideJobLocations && overrideJobLocations.length > 0) {
      jobLocation = overrideJobLocations;
    } else if (useFullCompanyAddress && matchingLocation) {
      // Use full company address when location matches
      const fullAddress = buildFullCompanyAddress(companyData);
      if (fullAddress) {
        jobLocation = [{
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            ...(fullAddress.streetAddress ? { streetAddress: fullAddress.streetAddress } : {}),
            ...(fullAddress.addressLocality ? { addressLocality: fullAddress.addressLocality } : {}),
            ...(fullAddress.postalCode ? { postalCode: fullAddress.postalCode } : {}),
            addressCountry: fullAddress.addressCountry,
          },
        }];
      } else {
        // Fallback to place entries if full address couldn't be built
        jobLocation = placeEntries.length > 0 ? placeEntries : undefined;
      }
    } else if (placeEntries.length > 0) {
      jobLocation = placeEntries;
    }
  }

  // A remote scope this wide (TEC-482's scopeType: 'global' resolves to every ISO country) isn't
  // a useful applicantLocationRequirements list — same reasoning the text heuristic below already
  // applies (mapRegionToRequirement collapses "worldwide"/"anywhere" to Finland, since our
  // audience is Finland-based, not a demand to enumerate every country on Earth).
  const REMOTE_ELIGIBLE_COUNTRY_LIST_CAP = 30;

  let applicantLocationRequirements: Record<string, unknown> | Record<string, unknown>[] | undefined =
    undefined;
  if (options.applicantLocationRequirements !== undefined) {
    applicantLocationRequirements = options.applicantLocationRequirements;
  } else if (jobLocationType === 'TELECOMMUTE') {
    // Prefer TEC-482's structured remote-eligibility resolution over parsing the location prose
    // ourselves: it already knows the actual scope (a single country, a named recruiting region,
    // a continent, or genuinely worldwide) instead of guessing from separator-split text. Only
    // fall back to the text heuristic when the field is entirely absent (job predates TEC-482
    // enrichment) or empty (the structured resolver ran but didn't capture a scope) — in both
    // cases we already know from the text-based remote detection above that this *is* a remote
    // job, so the text heuristic remains strictly better than emitting nothing.
    const structuredCountryCodes = job.remoteEligibleCountryCodes;
    if (Array.isArray(structuredCountryCodes) && structuredCountryCodes.length > 0) {
      if (structuredCountryCodes.length > REMOTE_ELIGIBLE_COUNTRY_LIST_CAP) {
        applicantLocationRequirements = { '@type': 'Country', name: 'Finland' };
      } else {
        const countryEntries = structuredCountryCodes.map((code) => ({
          '@type': 'Country',
          name: isoToCountryName[code] ?? code,
        }));
        applicantLocationRequirements = countryEntries.length === 1 ? countryEntries[0] : countryEntries;
      }
    } else {
      const dedupedRequirements = remoteRequirements.filter(
        (req, index, arr) =>
          index === arr.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(req))
      );

      if (dedupedRequirements.length > 0) {
        applicantLocationRequirements =
          dedupedRequirements.length === 1 ? dedupedRequirements[0] : dedupedRequirements;
      } else {
        // Remote job with no mappable region (including explicit worldwide), Google requires
        // applicantLocationRequirements on TELECOMMUTE jobs, so default to Finland (our audience).
        applicantLocationRequirements = { '@type': 'Country', name: 'Finland' };
      }
    }
  }

  // --- educationRequirements mapping to Google's expected values ---
  const educationRequirementsMap: Record<string, string> = {
    // English values
    'high school': 'high school',
    'vocational school': 'associate degree',
    'apprenticeship': 'associate degree',
    'bachelor': 'bachelor degree',
    'masters': 'postgraduate degree',
    'doctorate': 'postgraduate degree',
    'diploma': 'professional certificate',
    // Finnish values
    'toisen asteen koulutus': 'high school',
    'oppisopimuskoulutus': 'associate degree',
    'alempi korkeakoulututkinto': 'bachelor degree',
    'ylempi korkeakoulututkinto': 'postgraduate degree',
    'tieteellinen jatkotutkinto': 'postgraduate degree',
    'diplomi': 'professional certificate',
    // Swedish values
    'gymnasieutbildning': 'high school',
    'yrkesutbildning': 'associate degree',
    'lärlingsutbildning': 'associate degree',
    'kandidat': 'bachelor degree',
    'doktorand': 'postgraduate degree',
    'diplom': 'professional certificate',
  };
  
  // Convert educationLevel to proper schema format
  let educationRequirements: Record<string, unknown> | undefined = undefined;
  if (clean(job.educationLevel)) {
    const normalizedEducation = (job.educationLevel || '').toLowerCase().trim();
    const mappedValue = educationRequirementsMap[normalizedEducation];
    
    if (mappedValue) {
      educationRequirements = {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: mappedValue,
      };
    }
  }

  // --- specialCommitments for Finland/Europe ---
  const specialCommitments: string | undefined = undefined; // TODO: Add logic for VeteranCommit etc if applicable

  // --- incentiveCompensation ---
  // schema.org's incentiveCompensation means bonuses, commission, equity and the like. Our
  // salaryDetails text is usually just the base-pay note ("pay range EUR 3,500-4,200"), so it
  // only qualifies when it actually mentions an incentive component.
  const INCENTIVE_KEYWORDS =
    /\b(bonus\w*|commission\w*|incentive\w*|equity|stock|shares?|options?|profit[- ]sharing|provisio\w*|komissio\w*|kannustin\w*|tulospalkk\w*|osake\w*|optio\w*|provision\w*|aktie\w*|vinstdelning)\b/i;
  const salaryDetailsText = clean(job.salaryDetails);
  const incentiveCompensation: string | undefined =
    salaryDetailsText && INCENTIVE_KEYWORDS.test(salaryDetailsText) ? salaryDetailsText : undefined;

  // --- workHours ---
  const workHours: string | undefined = undefined; // TODO: Add more specific work hours if available

  // --- occupationalCategory ---
  const occupationalCategory: string | undefined = undefined; // TODO: Add occupational category if available

  // --- salary validation helpers ---
  const normalizeCurrency = (currency?: string): string | undefined => {
    if (!currency) return undefined;
    const trimmed = currency.trim().toUpperCase();
    return /^[A-Z]{3}$/.test(trimmed) ? trimmed : undefined;
  };

  const salaryPeriodMap: Record<string, 'YEAR' | 'MONTH' | 'WEEK' | 'DAY' | 'HOUR'> = {
    yearly: 'YEAR',
    annual: 'YEAR',
    year: 'YEAR',
    monthly: 'MONTH',
    month: 'MONTH',
    weekly: 'WEEK',
    week: 'WEEK',
    daily: 'DAY',
    day: 'DAY',
    hourly: 'HOUR',
    hour: 'HOUR',
  };

  const normalizeSalaryPeriod = (period?: string): 'YEAR' | 'MONTH' | 'WEEK' | 'DAY' | 'HOUR' | undefined => {
    if (!period) return undefined;
    return salaryPeriodMap[period.trim().toLowerCase()];
  };

  const SALARY_MAX_VALUE = 1_000_000_000; // Guardrail to avoid absurd placeholders
  const isValidSalaryNumber = (value?: number): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= SALARY_MAX_VALUE;

  // Default currency to EUR when absent (common for Finnish jobs with no currency set)
  const normalizedCurrency = normalizeCurrency(job.currency) ?? 'EUR';
  // Default period to MONTH only when the field is absent, if an unrecognised value was
  // explicitly set (e.g. "per-project"), leave it undefined so baseSalary is omitted.
  const normalizedUnit = normalizeSalaryPeriod(job.salaryPeriod) ?? (!job.salaryPeriod ? 'MONTH' : undefined);
  const hasMin = job.salaryRangeMin !== undefined && job.salaryRangeMin !== null;
  const hasMax = job.salaryRangeMax !== undefined && job.salaryRangeMax !== null;
  const minSalary = isValidSalaryNumber(job.salaryRangeMin) ? job.salaryRangeMin : undefined;
  const maxSalary = isValidSalaryNumber(job.salaryRangeMax) ? job.salaryRangeMax : undefined;
  const hasInvalidProvided =
    (hasMin && minSalary === undefined) || (hasMax && maxSalary === undefined);

  let baseSalary: Record<string, unknown> | undefined = undefined;

  if (
    normalizedCurrency &&
    normalizedUnit &&
    !hasInvalidProvided &&
    (minSalary !== undefined || maxSalary !== undefined)
  ) {
    // Reject inverted ranges
    if (minSalary !== undefined && maxSalary !== undefined && minSalary > maxSalary) {
      // Skip salary altogether rather than swapping implicitly
    } else {
      const salaryValue: Record<string, unknown> = {
        '@type': 'QuantitativeValue',
        unitText: normalizedUnit,
      };

      if (minSalary !== undefined && maxSalary !== undefined) {
        salaryValue.minValue = minSalary;
        salaryValue.maxValue = maxSalary;
        salaryValue.value = (minSalary + maxSalary) / 2;
      } else if (minSalary !== undefined) {
        salaryValue.value = minSalary;
      } else if (maxSalary !== undefined) {
        salaryValue.value = maxSalary;
      }

      baseSalary = {
        '@type': 'MonetaryAmount',
        currency: normalizedCurrency,
        value: salaryValue,
      };
    }
  }

  if (options.disableSalary) {
    baseSalary = undefined;
  }

  // --- hiringOrganization.sameAs ---
  // sameAs must identify the employer, so it is only ever the employer's own site. It used to
  // fall back to the apply link's origin, which for board- and ATS-hosted ads named the wrong
  // organisation entirely (tyomarkkinatori.fi, jobs.lever.co, ...). With no company URL the
  // property is omitted; Google neither requires nor recommends it.
  let sameAs: string | undefined = undefined;
  if (companyURL) {
    try {
      sameAs = new URL(companyURL).origin;
    } catch {
      sameAs = undefined;
    }
  }

  const canonicalClean = clean(canonicalUrl);
  const jobUrlClean = clean(job.url);
  const finalPageUrl = canonicalClean || jobUrlClean;

  const rawDescription = clean(job.originalDescription || job.description);
  const description = rawDescription ? formatJobPostingDescriptionHtml(rawDescription) : undefined;

  // Map DisplayJob fields to schema.org/JobPosting
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: clean(job.title || job.normalisedTitle),
    // Prefer original job text for schema; Google recommends the full original description
    description,
    datePosted,
    validThrough,
    employmentType,
    hiringOrganization: {
      '@type': 'Organization',
      name: clean(job.companyId),
      sameAs: sameAs,
      logo: logoUrl,
      description: clean(job.employerOverview),
      url: companyURL,
    },
    jobLocation,
    applicantLocationRequirements,
    // Only include jobLocationType for remote jobs (TELECOMMUTE)
    ...(jobLocationType ? { jobLocationType } : {}),
    ...(baseSalary ? { baseSalary } : {}),
    directApply: false, // TODO: Set to true if direct apply is supported
    identifier: {
      '@type': 'PropertyValue',
      name: clean(job.companyId),
      value: clean(job.objectID),
    },
    educationRequirements,
    experienceRequirements: clean(job.minimumYearsOfExperience)
      ? {
          '@type': 'OccupationalExperienceRequirements',
          monthsOfExperience: Number(job.minimumYearsOfExperience) * 12,
        }
      : undefined,
    experienceInPlaceOfEducation: clean(job.minimumYearsOfExperience) && !clean(job.educationLevel) ? true : undefined,
    incentiveCompensation,
    industry: clean(job.companyIndustry),
    jobBenefits: joinDisplayJobTextList(job.benefits),
    jobImmediateStart: undefined, // TODO: Add logic if available
    occupationalCategory: clean(job.field),
    physicalRequirement: job.isDriversLicenseRequired ? 'Driver\u2019s licence required' : undefined,
    qualifications: joinDisplayJobTextList(job.requirements),
    responsibilities: joinDisplayJobTextList(job.responsibilities),
    skills: joinDisplayJobTextList(job.skillsAndTechnologies),
    specialCommitments,
    totalJobOpenings: Number(job.numberOfPositions) > 0 ? Number(job.numberOfPositions) : 1,
    workHours,
    // Use the company logo we control; never use heroImage which may be an unrelated crawled asset
    image: logoUrl,
    // Use JobCrawls URL for ownership, not ATS URL
    ...(finalPageUrl ? { mainEntityOfPage: finalPageUrl } : {}),
    ...(finalPageUrl ? { url: finalPageUrl } : {}),
    inLanguage: locale,
  };

  const ensureIsoCountry = (place: any): boolean => {
    if (!place || typeof place !== 'object') return false;
    const address = place.address;
    if (!address || typeof address !== 'object') return false;
    const country = address.addressCountry;
    if (typeof country !== 'string') return false;
    return /^[A-Z]{2}$/.test(country.trim());
  };

  if (schema.jobLocationType && schema.jobLocationType !== 'TELECOMMUTE') {
    delete schema.jobLocationType;
  }

  if (schema.jobLocationType === 'TELECOMMUTE') {
    if (!remoteIndicated) {
      delete schema.jobLocationType;
      delete schema.applicantLocationRequirements;
    } else {
      // Google requires applicantLocationRequirements on TELECOMMUTE jobs; if the caller
      // explicitly cleared it (schemaOverrides), drop jobLocationType rather than emit an
      // invalid remote posting.
      if (!schema.applicantLocationRequirements) {
        delete schema.jobLocationType;
      }
      if (schema.jobLocation) {
        delete schema.jobLocation;
      }
    }
  } else {
    const locationsArray = Array.isArray(schema.jobLocation) ? schema.jobLocation : [];
    let hasIso = locationsArray.length > 0 && locationsArray.every(ensureIsoCountry);
    if (!hasIso) {
      const fallbackLocations =
        overrideJobLocations && overrideJobLocations.length > 0
          ? overrideJobLocations
          : placeEntries;
      const filtered = fallbackLocations.filter(ensureIsoCountry);
      if (filtered.length > 0) {
        schema.jobLocation = filtered;
        hasIso = true;
      }
    }
    if (!hasIso) {
      const hasLocationSignal =
        (Array.isArray(job.location) && job.location.some((loc) => String(loc).trim().length > 0)) ||
        placeEntries.length > 0 ||
        locationsArray.length > 0;
      if (hasLocationSignal && !remoteIndicated) {
        // TEC-482 Phase 8: the text-based countryMap/cityCountryMap dictionaries above only ever
        // covered English plus a handful of Finnish/Swedish names, so a location like "Hyderabad,
        // Intia" or "Lausanne, Schweiz" fails to resolve and used to fall through to assuming
        // Finland — silently mislabeling a real onsite/hybrid job abroad. When the structured
        // resolver has run for this job (job.locationCountryCodes present), trust it instead.
        const structuredCountryCodes = job.locationCountryCodes;
        if (Array.isArray(structuredCountryCodes)) {
          if (structuredCountryCodes.length === 1) {
            // Keep whatever locality/region text we already parsed (if any) rather than
            // discarding it just because the country segment didn't resolve.
            const bestGuessAddress = (placeEntries[0]?.address ?? {}) as Record<string, unknown>;
            schema.jobLocation = [
              {
                '@type': 'Place',
                address: {
                  '@type': 'PostalAddress',
                  ...(typeof bestGuessAddress.addressLocality === 'string'
                    ? { addressLocality: bestGuessAddress.addressLocality }
                    : {}),
                  ...(typeof bestGuessAddress.addressRegion === 'string'
                    ? { addressRegion: bestGuessAddress.addressRegion }
                    : {}),
                  addressCountry: structuredCountryCodes[0],
                },
              },
            ];
          } else {
            // 0 countries (structured resolution genuinely failed/needs review) or >1
            // (ambiguous multi-country posting) — omit jobLocation rather than fabricate one.
            delete schema.jobLocation;
          }
        } else {
          // Job predates TEC-482 enrichment (field entirely absent): keep the pre-existing
          // Finland-assuming behavior so historical data doesn't regress before a backfill can
          // run — see documentation/LOCATION_SYSTEM_ROLLOUT.md.
          schema.jobLocation = [buildFinlandCountryOnlyJobLocationPlace()];
        }
      } else {
        delete schema.jobLocation;
      }
    }
  }

  // List of all possible fields we try to fill
  const allFields = [
    'title',
    'description',
    'datePosted',
    'validThrough',
    'employmentType',
    'hiringOrganization',
    'jobLocation',
    'applicantLocationRequirements',
    'jobLocationType',
    'baseSalary',
    'directApply',
    'identifier',
    'educationRequirements',
    'experienceRequirements',
    'experienceInPlaceOfEducation',
    'incentiveCompensation',
    'industry',
    'jobBenefits',
    'jobImmediateStart',
    'occupationalCategory',
    'physicalRequirement',
    'qualifications',
    'responsibilities',
    'skills',
    'specialCommitments',
    'totalJobOpenings',
    'workHours',
    'image',
    'mainEntityOfPage',
    'url',
    'inLanguage',
  ];

  // Check for missing fields (required and recommended)
  const missingFields = allFields.filter((field) => {
    const val = schema[field];
    if (val === undefined || val === null) return true;
    if (typeof val === 'string' && val.trim() === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    // For objects, check if all values are undefined/null/empty string
    if (typeof val === 'object' && val !== null) {
      return Object.values(val).every((v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === ''));
    }
    return false;
  });

  const hasNonEmptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

  const hiringOrg = (schema.hiringOrganization ?? {}) as Record<string, unknown>;
  const identifier = (schema.identifier ?? {}) as Record<string, unknown>;
  const hasTelecommuteLocation = schema.jobLocationType === 'TELECOMMUTE';
  const hasOnsiteLocation = Array.isArray(schema.jobLocation) && schema.jobLocation.length > 0;
  const hasRemoteLocationRequirement = schema.applicantLocationRequirements !== undefined;

  const missingEssentialFields: string[] = [];
  if (!hasNonEmptyString(schema.title)) missingEssentialFields.push('title');
  if (!hasNonEmptyString(schema.description)) missingEssentialFields.push('description');
  if (!hasNonEmptyString(schema.datePosted)) missingEssentialFields.push('datePosted');
  if (!hasNonEmptyString(schema.validThrough)) missingEssentialFields.push('validThrough');
  // employmentType is recommended, not required, by Google; an unmappable jobType omits it and
  // must not block indexing.
  if (!hasNonEmptyString(hiringOrg.name)) missingEssentialFields.push('hiringOrganization.name');
  if (!hasNonEmptyString(identifier.value)) missingEssentialFields.push('identifier.value');
  if (!hasNonEmptyString(schema.url)) missingEssentialFields.push('url');
  if (!hasNonEmptyString(schema.mainEntityOfPage)) missingEssentialFields.push('mainEntityOfPage');
  // inLanguage is deliberately not essential: schema.org does not define it on JobPosting and
  // Google neither requires nor recommends it, so it must never gate indexing.
  if (hasTelecommuteLocation) {
    if (!hasRemoteLocationRequirement) {
      missingEssentialFields.push('applicantLocationRequirements');
    }
  } else if (!hasOnsiteLocation) {
    missingEssentialFields.push('jobLocation');
  }

  const isContentComplete = missingEssentialFields.length === 0;

  return { schema, isContentComplete, missingFields };
}
